<template>
  <div>
    <change-email-form
      :email="state?.context.emailToAdd"
      :errors="state?.context.errors"
      :is-submitting="state?.matches('action.add.adding')"
      @change="updateEmailToAdd($event)"
      @submit="add($event)"
      @cancel="$emit('cancel')"
    />

    <change-email-pending-emails
      v-if="state"
      :verified-emails="verifiedEmails"
      :unverified-emails="unverifiedEmails"
      :is-removing="isRemoving"
      :is-fetching="isFetching"
      @remove="remove($event)"
      @resend="resend($event)"
    />
    <translate v-if="state?.matches('error')" tag="p">
      Something went wrong, try again later
    </translate>
  </div>
</template>

<script lang="ts">
import { Component, Vue, toNative, Setup } from '~/utility/pu-class-decorator'
import { interpret } from 'xstate-v4'
import {
  ChangeEmailMachineInterpreter,
  returnChangeEmailMachine,
} from './machine'
import { UserEmail } from './UserPendingEmailConfirmationsRepositoryFetch'
@Component
class ChangeEmailMain extends Vue {
  state: ChangeEmailMachineInterpreter['state'] | null = null

  @Setup(() => {
    return returnChangeEmailMachine()
  })
  machine: ChangeEmailMachineInterpreter

  verifiedEmails: UserEmail[] = []
  unverifiedEmails: UserEmail[] = []

  isRemoving = false
  isFetching = false

  mounted() {
    this.machine = interpret(returnChangeEmailMachine())
    this.startMachine()
  }

  add(email: string) {
    this.machine.send('ADD', { email })
  }

  resend(emailId: number) {
    this.machine.send('RESEND_CONFIRMATION_EMAIL', { email: emailId })
  }

  remove(email: string) {
    this.machine.send('REMOVE', { email })
  }

  updateEmailToAdd(email: string) {
    this.machine.send('UPDATE_EMAIL_TO_ADD', { email })
  }

  async showInfoAboutConfirmationEmailSent(email: string) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call -- $getSnackbar returns untyped snackbar service
    const snack = await this.$getSnackbar()
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- $getSnackbar returns untyped snackbar service
    snack.show({
      text: this.$t('Confirmation email sent to {email}', { email }),
      time: 2000,
    })
  }

  private startMachine() {
    this.machine
      // eslint-disable-next-line @typescript-eslint/no-misused-promises -- XState onTransition accepts async callbacks
      .onTransition(async (state) => {
        this.state = state
        this.setFetchedEmails(state)
        this.setCurrentStates(state)
        if (state.matches('action.add.added')) {
          await this.showInfoAboutConfirmationEmailSent(
            state.context.emailAdded,
          )
        }
      })
      .start()
  }

  private setFetchedEmails(state: ChangeEmailMachineInterpreter['state']) {
    this.verifiedEmails =
      Object.values(state.context.pendingEmails).filter((e) => e.verified) || []

    this.unverifiedEmails = Object.values(state.context.pendingEmails).filter(
      (e) => !e.verified,
    )
  }

  private setCurrentStates(state: ChangeEmailMachineInterpreter['state']) {
    this.isRemoving = state.matches('action.remove.removing')

    this.isFetching = state.matches('fetching')
  }
}

export default toNative(ChangeEmailMain)
</script>
<style scoped>
p {
  font-size: var(--body);
}

.edit-email {
  z-index: 1;
  position: fixed;
  margin: var(--edge-margin);
  padding: 1em 1.4em;
  top: 2em;
  left: 0;
  right: 0;
}

.edit-email:before {
  background: rgba(0, 0, 0, 0.5);
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  bottom: 0;
  right: 0;
  pointer-events: all;
}

.edit-email:after {
  background: white;
  content: '';
  width: 100%;
  height: 100%;
  z-index: -1;
  display: block;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  border-radius: var(--border-radius);
  box-shadow: var(--card-box-shadow);
}
</style>
